import DefaultIcon from '../../assets/images/default icon.png';

// TypeScript interfaces
interface ImageModule {
  default: string;
}

interface ImageMap {
  [key: string]: string;
}

/**
 * Valid image folder types for provider/sensor image resolution
 */
type ImageFolderType = 'transportProvider' | 'SensorImages';

/**
 * Supported image file extensions for dynamic import resolution
 */
type SupportedExtension = 'svg' | 'png' | 'jpg' | 'jpeg' | 'webp';

// Image storage maps populated during module initialization
const transportProviderImages: ImageMap = {};
const sensorImages: ImageMap = {};

/**
 * Initialize transport provider images from dynamic imports
 *
 * Load all transport provider images from the assets folder and create a
 * filename-to-path mapping.
 */
try {
  const transportProviderModules = import.meta.glob(
    '../../assets/images/transportProvider/*.{png,jpg,jpeg,svg,webp}',
    { eager: true }
  ) as Record<string, ImageModule>;

  Object.entries(transportProviderModules).forEach(([path, module]) => {
    const fileName = path.split('/').pop()?.split('.')[0];
    if (fileName) {
      transportProviderImages[fileName] = module.default;
    }
  });
} catch (err) {
  console.warn('Error loading transport provider images:', err);
}

/**
 * Initialize sensor images from dynamic imports
 *
 * Load all sensor images from the assets folder and create a filename-to-path
 * mapping.
 */
try {
  const sensorModules = import.meta.glob(
    '../../assets/images/SensorImages/*.{png,jpg,jpeg,svg,webp}',
    { eager: true }
  ) as Record<string, ImageModule>;

  Object.entries(sensorModules).forEach(([path, module]) => {
    const fileName = path.split('/').pop()?.split('.')[0];
    if (fileName) {
      sensorImages[fileName] = module.default;
    }
  });
} catch (err) {
  console.warn('Error loading sensor images:', err);
}

/**
 * List of supported image file extensions for fallback resolution
 *
 * Used when attempting to match image files with different extensions
 * during the image path resolution process.
 */
const supportedExtensions: SupportedExtension[] = [
  'svg',
  'png',
  'jpg',
  'jpeg',
  'webp',
];

/** Titles of the bundled transport provider logos. */
export const transportProviderTitles = (): string[] =>
  Object.keys(transportProviderImages);

/**
 * Returns the appropriate image path for a provider or sensor, falling back to a default icon if not found.
 *
 * This function performs a multi-step resolution process:
 * 1. Validates input parameters and returns default icon for invalid inputs
 * 2. Applies provider-specific name mappings (e.g., "Time:Matters" → "time-matters")
 * 3. Attempts exact filename match in the appropriate image map
 * 4. Performs case-insensitive filename matching
 * 5. Tries various file extensions with case-insensitive matching
 * 6. Falls back to default icon if no matches found
 *
 * @param name - The name of the provider or sensor to find an image for
 * @param type - The image folder type ('transportProvider' or 'SensorImages')
 * @returns The resolved image path string or default icon path
 */
export const getProviderImagePath = (
  name: string | null | undefined,
  type: ImageFolderType | null | undefined
): string => {
  // Return default icon for invalid inputs
  if (!name || !type) return DefaultIcon;

  let imageFileName: string = name;
  let imagesMap: ImageMap;

  // Determine correct context and normalize name
  if (type === 'transportProvider') {
    imagesMap = transportProviderImages;
    if (name === 'Time:Matters') {
      imageFileName = 'time-matters';
    } else if (name === 'ODFL') {
      imageFileName = 'Old Dominion Freight Lines';
    }
    // Add other transport-specific name adjustments here
  } else if (type === 'SensorImages') {
    imagesMap = sensorImages;
    if (name === 'Sensitech / TempTale' || name === 'Sensitech') {
      imageFileName = 'Sensitech-Temptale';
    }
  } else {
    // Invalid type provided - return default icon
    return DefaultIcon;
  }

  // Start with the mapped filename (preserving original case/format for now)
  const normalizedFileName: string = imageFileName;

  // Step 1: Try exact filename match
  if (imagesMap[normalizedFileName]) {
    return imagesMap[normalizedFileName];
  }

  // Step 2: Try case-insensitive filename match
  const caseInsensitiveMatch = Object.keys(imagesMap).find(
    (key: string) => key.toLowerCase() === normalizedFileName.toLowerCase()
  );

  if (caseInsensitiveMatch) {
    return imagesMap[caseInsensitiveMatch];
  }

  // Step 3: Try different file extensions with case-insensitive matching
  for (const ext of supportedExtensions) {
    const potentialKey = `${normalizedFileName}.${ext}`.toLowerCase();
    const extensionMatch = Object.keys(imagesMap).find(
      (key: string) => key.toLowerCase() === potentialKey
    );

    if (extensionMatch) {
      return imagesMap[extensionMatch];
    }
  }

  // Step 4: No matches found - return default icon
  return DefaultIcon;
};
